import { logEvent, type Analytics } from 'firebase/analytics';
import type { WaitlistCaptureSurface } from '../types/waitlist';
import { hasAnalyticsConsent } from './analyticsConsent';
import { whenAnalyticsReady } from './analyticsInstance';

/**
 * The Google Ads conversion for the waitlist: one `waitlist_signup` per saved
 * entry, sent before any payment step. Never `sign_up`, which belongs to
 * account creation.
 *
 * Only the surface goes to Google. No email, name or entry ref, ever.
 *
 * joinWaitlist answers a repeat email with the same ref, so refs already sent
 * are remembered in localStorage and skipped. Without consent nothing is
 * sent, queued or remembered, so a visitor who accepts later and joins again
 * is still counted once. With consent but no instance yet, the send waits for
 * the instance.
 *
 * This is the one place a Meta Lead event would be added.
 */
export const TRACKED_REFS_KEY = 'melio_waitlist_tracked';
const MAX_TRACKED_REFS = 20;

/** The refs already sent, or null when storage cannot be read. */
function readTracked(): string[] | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(TRACKED_REFS_KEY);
  } catch {
    return null;
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((r): r is string => typeof r === 'string') : [];
  } catch {
    // Corrupt: start afresh, so the next write repairs it instead of
    // switching dedupe off for this browser for good.
    return [];
  }
}

function writeTracked(refs: string[]): void {
  try {
    window.localStorage.setItem(TRACKED_REFS_KEY, JSON.stringify(refs.slice(-MAX_TRACKED_REFS)));
  } catch {
    // Storage blocked: a rare double count beats a lost conversion.
  }
}

function send(analytics: Analytics, ref: string, surface: WaitlistCaptureSurface): void {
  try {
    // The instance outlives the choice that loaded it, and a queued send runs
    // later, so ask again here: a visitor who has since declined must not be
    // counted.
    if (!hasAnalyticsConsent()) return;
    const tracked = readTracked();
    if (tracked && tracked.includes(ref)) return;
    logEvent(analytics, 'waitlist_signup', { source_surface: surface });
    if (tracked) writeTracked([...tracked, ref]);
  } catch {
    // Analytics must never get in the way of a signup.
  }
}

export function trackWaitlistSignup(ref: string, surface: WaitlistCaptureSurface): void {
  try {
    if (!hasAnalyticsConsent()) return;
    // Consent can be a moment ahead of the instance (it is created
    // asynchronously after Accept), so a signup in that gap is sent once the
    // instance exists rather than dropped.
    whenAnalyticsReady((analytics) => send(analytics, ref, surface));
  } catch {
    // Analytics must never get in the way of a signup.
  }
}
